import { useParams, useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/use-toast'
import {
	CheckCircle2,
	FileText,
	Upload,
	Settings,
} from 'lucide-react'
import confetti from 'canvas-confetti'
import { useEffect, useState } from 'react'
import { CodeBlock } from '@/components/docs/installation/CodeBlock'
import { useTranslation } from '@/hooks/useTranslation'

export default function WidgetSuccess() {
	const { t } = useTranslation()
	const { widgetId } = useParams()
	const navigate = useNavigate()
	const [copied, setCopied] = useState(false)

	useEffect(() => {
		// Trigger confetti when component mounts
		const duration = 1500
		const animationEnd = Date.now() + duration
		const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 }

		const randomInRange = (min: number, max: number) => {
			return Math.random() * (max - min) + min
		}

		const interval: any = setInterval(() => {
			const timeLeft = animationEnd - Date.now()

			if (timeLeft <= 0) {
				return clearInterval(interval)
			}

			const particleCount = 50 * (timeLeft / duration)

			// Since particles fall down, start a bit higher than random
			confetti({
				...defaults,
				particleCount,
				origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
				colors: [
					'#26ccff',
					'#a25afd',
					'#ff5e7e',
					'#88ff5a',
					'#fcff42',
					'#ffa62d',
					'#ff36ff',
				],
			})
			confetti({
				...defaults,
				particleCount,
				origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
				colors: [
					'#26ccff',
					'#a25afd',
					'#ff5e7e',
					'#88ff5a',
					'#fcff42',
					'#ffa62d',
					'#ff36ff',
				],
			})
		}, 250)

		return () => clearInterval(interval)
	}, [])

	const copyEmbedCode = () => {
		const embedCode = `<script
	defer
	src="https://cdn.feedbask.com/widget.js"
	data-client-key="${widgetId}"
	id="feedbask-widget-script"
></script>`

		navigator.clipboard
			.writeText(embedCode)
			.then(() => {
				setCopied(true)
				setTimeout(() => setCopied(false), 2000)
			})
			.catch(() => {
				toast({
					title: t('widgetSuccess.error'),
					description: t('widgetSuccess.failedToCopyEmbedCode'),
					variant: 'destructive',
				})
			})
	}

	const embedCode = `<script
	defer
	src="https://cdn.feedbask.com/widget.js"
	data-client-key="${widgetId}"
	id="feedbask-widget-script"
></script>`

	return (
		<div className="min-h-screen p-4 md:p-8">
			<div className="max-w-3xl mx-auto">
				<div className="text-center mb-12">
					<div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-600 text-white mb-4">
						<CheckCircle2 className="h-8 w-8 " />
					</div>
					<h1 className="text-2xl font-medium text-gray-400 mb-2">{t('widgetSuccess.amazing')}</h1>
					<h2 className="text-3xl font-bold mb-4">
						{t('widgetSuccess.congratulations')}
					</h2>
				</div>

				<Card className="p-6 md:p-8 shadow-lg mb-8">
					<h3 className="text-xl font-semibold mb-6">{t('widgetSuccess.embedYourWidget')}</h3>
					<div className="space-y-4 mb-6">
						<p className="text-gray-600">
							{t('widgetSuccess.addWidgetThreeSteps')}
						</p>
						<ol className="list-decimal list-inside space-y-3 text-gray-600">
							<li className="pl-2">
								<span className="font-medium text-gray-900">{t('widgetSuccess.copy')}</span> {t('widgetSuccess.copyCodeDescription')}
							</li>
							<li className="pl-2">
								<span className="font-medium text-gray-900">{t('widgetSuccess.paste')}</span> {t('widgetSuccess.pasteCodeDescription')}{' '}
								<code className="text-sm bg-gray-100 px-1 py-0.5 rounded">
									&lt;/body&gt;
								</code>{' '}
								{t('widgetSuccess.tagOfWebsite')}
							</li>
							<li className="pl-2">
								<span className="font-medium text-gray-900">{t('widgetSuccess.save')}</span> {t('widgetSuccess.saveAndRefresh')}
							</li>
						</ol>
					</div>
					<div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
						<p className="text-sm text-blue-700 flex items-start gap-2">
							<span className="text-blue-500 mt-0.5">💡</span>
							<span>
								<span className="font-semibold">{t('widgetSuccess.needHelp')}</span> {t('widgetSuccess.shareCodeOrCheckGuide')}{' '}
								<button
									onClick={() => window.open('/docs', '_blank')}
									className="text-blue-600 hover:underline">
									{t('widgetSuccess.checkOurGuide')}
								</button>
								.
							</span>
						</p>
					</div>
					<div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg mb-4 font-mono text-sm overflow-x-auto border">
						<CodeBlock code={embedCode} language="html" />
					</div>
					{/* <Button
						onClick={copyEmbedCode}
						variant={copied ? 'outline' : 'secondary'}
						className={`w-full sm:w-auto transition-all duration-200 ${
							copied ? 'bg-green-50 text-green-600 border-green-200' : ''
						}`}>
						{copied ? (
							<>
								<CheckCircle2 className="mr-2 h-4 w-4" />
								Copied!
							</>
						) : (
							<>
								<Copy className="mr-2 h-4 w-4" />
								Copy Code
							</>
						)}
					</Button> */}
				</Card>

				<div className="text-center mb-8">
					<h3 className="text-xl font-semibold mb-2">{t('widgetSuccess.whatsNext')}</h3>
				</div>

				<div className="grid gap-6 md:grid-cols-3 mb-12">
					<div className="bg-white p-6 rounded-lg shadow-sm border">
						<div className="text-gray-400 mb-2">{t('widgetSuccess.step1')}</div>
						<h4 className="font-medium mb-2">{t('widgetSuccess.configureWidget')}</h4>
						<p className="text-sm text-gray-500 mb-4">
							{t('widgetSuccess.customizeAppearance')}
						</p>
						<Button
							variant="outline"
							className="w-full"
							onClick={() => navigate(`/widgets/${widgetId}`)}>
							<Settings className="mr-2 h-4 w-4" />
							{t('widgetSuccess.configure')}
						</Button>
					</div>
					<div className="bg-white p-6 rounded-lg shadow-sm border">
						<div className="text-gray-400 mb-2">{t('widgetSuccess.step2')}</div>
						<h4 className="font-medium mb-2">{t('widgetSuccess.addToWebsite')}</h4>
						<p className="text-sm text-gray-500 mb-4">
							{t('widgetSuccess.copyPasteEmbedCode')}
						</p>
						<Button
							variant="outline"
							className="w-full"
							onClick={copyEmbedCode}>
							<FileText className="mr-2 h-4 w-4" />
							{t('widgetSuccess.getCode')}
						</Button>
					</div>
					<div className="bg-white p-6 rounded-lg shadow-sm border">
						<div className="text-gray-400 mb-2">{t('widgetSuccess.step3')}</div>
						<h4 className="font-medium mb-2">{t('widgetSuccess.testWidget')}</h4>
						<p className="text-sm text-gray-500 mb-4">
							{t('widgetSuccess.previewTestWidget')}
						</p>
						<Button
							variant="outline"
							className="w-full"
							onClick={() => window.open(`/embed/${widgetId}`, '_blank')}>
							<Upload className="mr-2 h-4 w-4" />
							{t('widgetSuccess.preview')}
						</Button>
					</div>
				</div>

				<div className="text-center">
					<Button
						variant="default"
						size="lg"
						onClick={() => navigate('/dashboard')}
						className="px-8">
						{t('widgetSuccess.goToDashboard')}
					</Button>
				</div>
			</div>
		</div>
	)
}
